import { normalizeAutomation } from "./graph";
import type { HospitalAutomation } from "./types";

const version = 2;

type AutomationStore = {
  version: number;
  items: HospitalAutomation[];
};

function keyFor(hospitalId: string) {
  return `stathospital.automations.v${version}.${hospitalId}`;
}

function emptyStore(): AutomationStore {
  return { version, items: [] };
}

function migrateLegacy(hospitalId: string): AutomationStore | null {
  try {
    const legacy = window.localStorage.getItem(
      `stathospital.automations.v1.${hospitalId}`,
    );
    if (!legacy) return null;
    const parsed = JSON.parse(legacy) as AutomationStore;
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return {
      version,
      items: parsed.items.map((item) =>
        normalizeAutomation({ ...item, edges: item.edges || [] }),
      ),
    };
  } catch {
    return null;
  }
}

function readStore(hospitalId: string): AutomationStore {
  if (!hospitalId || typeof window === "undefined") return emptyStore();
  try {
    const raw = window.localStorage.getItem(keyFor(hospitalId));
    if (!raw) {
      const migrated = migrateLegacy(hospitalId);
      if (migrated) {
        writeStore(hospitalId, migrated);
        return migrated;
      }
      return emptyStore();
    }
    const parsed = JSON.parse(raw) as AutomationStore;
    if (!parsed || !Array.isArray(parsed.items)) return emptyStore();
    return {
      version,
      items: parsed.items.map((item) =>
        normalizeAutomation({ ...item, edges: item.edges || [] }),
      ),
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(hospitalId: string, store: AutomationStore) {
  window.localStorage.setItem(keyFor(hospitalId), JSON.stringify(store));
}

export function listAutomations(hospitalId: string): HospitalAutomation[] {
  return readStore(hospitalId).items.sort((a, b) =>
    String(b.updatedAt).localeCompare(String(a.updatedAt)),
  );
}

export function getAutomation(hospitalId: string, id: string) {
  return listAutomations(hospitalId).find((item) => item.id === id) || null;
}

export function upsertAutomation(automation: HospitalAutomation) {
  const store = readStore(automation.hospitalId);
  const next: HospitalAutomation = {
    ...normalizeAutomation(automation),
    name: automation.name.trim() || "Untitled automation",
    updatedAt: new Date().toISOString(),
  };
  const index = store.items.findIndex((item) => item.id === next.id);
  if (index >= 0) store.items[index] = next;
  else store.items.unshift(next);
  writeStore(automation.hospitalId, store);
  return next;
}

export function deleteAutomation(hospitalId: string, id: string) {
  const store = readStore(hospitalId);
  store.items = store.items.filter((item) => item.id !== id);
  writeStore(hospitalId, store);
}

export function setAutomationEnabled(
  hospitalId: string,
  id: string,
  enabled: boolean,
) {
  const store = readStore(hospitalId);
  store.items = store.items.map((item) =>
    item.id === id
      ? { ...item, enabled, updatedAt: new Date().toISOString() }
      : item,
  );
  writeStore(hospitalId, store);
}
